import {
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useState,
  useMemo,
} from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { RootState } from 'src/redux/rootReducerType';
import { setSavedInitials } from 'src/redux/slices/NewRenterInterface/NewRenterInterfaceUserSlice';
import { addAlert } from 'src/redux/slices/Compliance/CertificationAlertsSlice';
import GoogleFontIcon from 'src/icons/GoogleFontIcon';
import axios from 'src/utils/axios';
import SignaturePadModal from './SignaturePadModal';
import type { SignatureResult } from './SignaturePadModal';
import { buildStandaloneInkXfdf } from './xfdfSerializer';
import { parseXfdfPreview } from './xfdfPreview';

const SIGNATURE_FONTS = [
  'Dancing Script',
  'Allura',
  'Alex Brush',
  'Yellowtail',
  'Petit Formal Script',
];

const GOOGLE_FONTS_URL =
  'https://fonts.googleapis.com/css2?' +
  SIGNATURE_FONTS.map((f) => `family=${f.replace(/ /g, '+')}`).join('&') +
  '&display=swap';

const FONTS_LINK_ID = 'saved-initials-fonts';

function ensureFontsLoaded(): void {
  if (document.getElementById(FONTS_LINK_ID)) {
    return;
  }
  const link = document.createElement('link');
  link.id = FONTS_LINK_ID;
  link.rel = 'stylesheet';
  link.href = GOOGLE_FONTS_URL;
  document.head.appendChild(link);
}

function XfdfInkPreview({
  viewBox,
  paths,
}: {
  viewBox: string;
  paths: string[];
}): ReactNode {
  return (
    <svg viewBox={viewBox} style={{ width: '100%', height: '100%' }}>
      {paths.map((d, i) => (
        <path
          key={i}
          d={d}
          stroke="currentColor"
          strokeWidth="2"
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      ))}
    </svg>
  );
}

interface SavedInitialsCardProps {
  isImpersonated: boolean;
}

const SavedInitialsCard: FC<SavedInitialsCardProps> = ({ isImpersonated }) => {
  useEffect(() => {
    ensureFontsLoaded();
  }, []);

  const dispatch = useDispatch();
  const theme = useTheme();
  const iconColor =
    theme.palette.mode === 'dark' ? 'text.primary' : 'text.secondary';
  const initialsText = useSelector(
    (state: RootState) => state.newRenterInterfaceUser.savedInitialsText
  );
  const initialsFont = useSelector(
    (state: RootState) => state.newRenterInterfaceUser.savedInitialsFont
  );
  const initialsXfdf = useSelector(
    (state: RootState) => state.newRenterInterfaceUser.savedInitialsXfdf
  );
  const userName =
    useSelector(
      (state: RootState) => state.newRenterInterfaceUser.user?.displayName
    ) ?? '';

  const preview = useMemo(() => {
    if (initialsXfdf) {
      return parseXfdfPreview(initialsXfdf);
    }
    if (initialsText) {
      return {
        type: 'text' as const,
        text: initialsText,
        fontFamily: initialsFont || 'Dancing Script',
      };
    }
    return null;
  }, [initialsXfdf, initialsText, initialsFont]);

  const [viewOpen, setViewOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleViewOpen = useCallback(() => {
    setViewOpen(true);
  }, []);

  const handleViewClose = useCallback(() => {
    setViewOpen(false);
  }, []);

  const handleModalOpen = useCallback(() => {
    setModalOpen(true);
  }, []);

  const handleModalClose = useCallback(() => {
    setModalOpen(false);
  }, []);

  const handleDeleteOpen = useCallback(() => {
    setDeleteOpen(true);
  }, []);

  const handleDeleteClose = useCallback(() => {
    setDeleteOpen(false);
  }, []);

  const handleModalDone = useCallback(
    async (result: SignatureResult) => {
      setModalOpen(false);

      setSaving(true);
      try {
        if (result.type === 'ink') {
          const xfdf = buildStandaloneInkXfdf(result.strokes, userName);
          await axios.put('signature/saved_initials/', {
            signature_data: xfdf,
          });
          dispatch(setSavedInitials({ xfdf }));
        } else {
          await axios.put('signature/saved_initials/', {
            initials_text: result.text,
            font_family: result.fontFamily,
          });
          dispatch(
            setSavedInitials({ text: result.text, font: result.fontFamily })
          );
        }
      } catch {
        dispatch(
          addAlert({
            context: 'renter',
            alert: {
              type: 'general',
              severity: 'error',
              message: 'Failed to save initials. Please try again.',
              timeout: 5000,
            },
          })
        );
      } finally {
        setSaving(false);
      }
    },
    [dispatch, userName]
  );

  const handleDelete = useCallback(async () => {
    setDeleting(true);
    try {
      await axios.delete('signature/saved_initials/');
      dispatch(setSavedInitials({ text: null, font: null }));
    } catch {
      dispatch(
        addAlert({
          context: 'renter',
          alert: {
            type: 'general',
            severity: 'error',
            message: 'Failed to delete initials. Please try again.',
            timeout: 5000,
          },
        })
      );
    } finally {
      setDeleting(false);
      setDeleteOpen(false);
    }
  }, [dispatch]);

  const hasInitials = !!initialsText || !!initialsXfdf;

  const renderContent = (): ReactNode => {
    if (saving) {
      return (
        <>
          <CircularProgress size={16} />
          <Typography variant="body2" color="text.secondary">
            Saving…
          </Typography>
        </>
      );
    }

    if (hasInitials) {
      return (
        <>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <CheckCircleIcon
              sx={(theme) => ({
                fontSize: 16,
                color:
                  theme.palette.mode === 'dark'
                    ? 'primary.light'
                    : 'primary.main',
              })}
            />
            <Typography
              variant="body2"
              sx={(theme) => ({
                color:
                  theme.palette.mode === 'dark'
                    ? 'primary.light'
                    : 'primary.main',
              })}
              fontWeight={500}
            >
              Saved initials
            </Typography>
          </Stack>
          {!isImpersonated && (
            <Stack direction="row" spacing={0.5}>
              <Tooltip title="View">
                <IconButton size="small" onClick={handleViewOpen}>
                  <GoogleFontIcon
                    name="visibility"
                    sx={{ fontSize: 18, color: iconColor }}
                  />
                </IconButton>
              </Tooltip>
              <Tooltip title="Edit">
                <IconButton size="small" onClick={handleModalOpen}>
                  <GoogleFontIcon
                    name="edit"
                    sx={{ fontSize: 18, color: iconColor }}
                  />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete">
                <IconButton size="small" onClick={handleDeleteOpen}>
                  <GoogleFontIcon
                    name="delete"
                    sx={{ fontSize: 18, color: iconColor }}
                  />
                </IconButton>
              </Tooltip>
            </Stack>
          )}
        </>
      );
    }

    return (
      <>
        <Typography variant="body2" color="text.secondary">
          Add initials
        </Typography>
        {!isImpersonated && (
          <Tooltip title="Add initials">
            <IconButton size="small" onClick={handleModalOpen}>
              <GoogleFontIcon
                name="add"
                sx={{ fontSize: 18, color: iconColor }}
              />
            </IconButton>
          </Tooltip>
        )}
      </>
    );
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          flex: 1,
          border: 1,
          borderColor: 'grey.500',
          borderRadius: 2,
          backgroundColor: 'background.paper',
          px: 1.5,
          py: 1.5,
        }}
      >
        {renderContent()}
      </Box>

      <Dialog open={viewOpen} onClose={handleViewClose} maxWidth="xs" fullWidth>
        <DialogTitle>Saved Initials</DialogTitle>
        <DialogContent>
          {preview && (
            <Box
              sx={{
                width: '100%',
                height: 120,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                p: 2,
                color: 'text.primary',
              }}
            >
              {preview.type === 'ink' ? (
                <XfdfInkPreview
                  viewBox={preview.viewBox}
                  paths={preview.paths}
                />
              ) : (
                <Typography
                  sx={{
                    fontFamily: `"${preview.fontFamily}", cursive`,
                    fontSize: '2rem',
                  }}
                >
                  {preview.text}
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleViewClose}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteOpen} onClose={handleDeleteClose} maxWidth="xs">
        <DialogTitle>Delete Saved Initials</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete your saved initials? You will need
            to enter them again next time.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteClose} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            disabled={deleting}
          >
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <SignaturePadModal
        open={modalOpen}
        onClose={handleModalClose}
        onDone={handleModalDone}
        userName={userName}
        hasSavedInitials={hasInitials}
        hideCheckbox={true}
        initialTab={0}
        mode="initials"
      />
    </>
  );
};

export default SavedInitialsCard;
